import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, fmtDate } from "../../lib/api";
import { ClipboardCheck, CalendarDays, Users, UserCheck, Clock, UserX } from "lucide-react";
import { localeOf } from "../../i18n";

const Stat = ({ icon: Icon, label, value, testId, accent }) => (
  <div className="vy-card p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon className={`h-4 w-4 ${accent ? "text-accent" : ""}`} />
      <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
    </div>
    <p className="mt-2 font-display text-3xl font-extrabold">{value ?? 0}</p>
  </div>
);

export default function StaffDashboard() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const locale = localeOf(i18n.language);

  useEffect(() => { api.get("/dashboard/staff").then(({ data }) => setData(data)); }, []);
  const s = data?.stats || {};

  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      <h1 className="font-display text-3xl font-extrabold">{t("nav.dashboard")}</h1>

      {s.needs_review > 0 && (
        <Link to="/staff/review" data-testid="needs-review-banner"
          className="flex items-center gap-3 rounded-2xl bg-accent p-5 text-accent-foreground transition-transform active:scale-[0.99]">
          <ClipboardCheck className="h-6 w-6" />
          <div>
            <p className="font-display text-lg font-bold">{s.needs_review} · {t("review.needsReview")}</p>
            <p className="text-sm opacity-90">{t("review.queue")} →</p>
          </div>
        </Link>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={ClipboardCheck} label={t("review.needsReview")} value={s.needs_review} testId="stat-needs-review" accent />
        <Stat icon={CalendarDays} label={t("events.upcoming")} value={s.events_upcoming} testId="stat-events-upcoming" />
        <Stat icon={UserCheck} label={t("events.checkedIn")} value={s.checked_in} testId="stat-checked-in" />
        <Stat icon={Clock} label={t("status.waitlisted")} value={s.waitlisted} testId="stat-waitlisted" />
        <Stat icon={Users} label={t("status.confirmed")} value={s.confirmed} testId="stat-confirmed" />
        <Stat icon={UserX} label={t("profile.noShows")} value={s.no_shows} testId="stat-no-shows" />
        {s.users_total !== undefined && (
          <>
            <Stat icon={Users} label={t("nav.users")} value={s.users_total} testId="stat-users" />
            <Stat icon={CalendarDays} label={t("nav.campaigns")} value={s.campaigns} testId="stat-campaigns" />
          </>
        )}
      </div>

      <section className="vy-card p-5">
        <h2 className="font-display text-lg font-bold">{t("events.upcoming")}</h2>
        <ul className="mt-3 divide-y divide-border">
          {(data?.upcoming || []).map((e) => (
            <li key={e.id} className="py-3">
              <Link to={`/staff/events/${e.id}`} data-testid={`dashboard-event-${e.id}`}
                className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold">{e.title}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(e.starts_at, locale)} · {e.location}</p>
                </div>
                <span className="rounded-full bg-muted px-3 py-1 text-xs font-bold">{e.short_code}</span>
              </Link>
            </li>
          ))}
          {(data?.upcoming || []).length === 0 && (
            <li className="py-3 text-sm text-muted-foreground">{t("events.noEvents")}</li>
          )}
        </ul>
      </section>
    </div>
  );
}
